import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import { useForm } from 'react-hook-form'
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    PhotoIcon,
    EyeIcon,
    XMarkIcon,
    PlayIcon
} from '@heroicons/react/24/outline'
import { bannerAPI } from '../../services/api'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ErrorMessage from '../../components/Common/ErrorMessage'
import ImageUpload from '../../components/Common/ImageUpload'
import PermissionWrapper from '../../components/Admin/PermissionWrapper'
import toast from 'react-hot-toast'

const AdminBanners = () => {
    return (
        <PermissionWrapper requiredPermission="banners">
            <AdminBannersContent />
        </PermissionWrapper>
    )
}

const AdminBannersContent = () => {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [editingBanner, setEditingBanner] = useState(null)
    const queryClient = useQueryClient()

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['admin-banners'],
        queryFn: bannerAPI.getBanners,
    })

    const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm()

    const createMutation = useMutation({
        mutationFn: bannerAPI.createBanner,
        onSuccess: () => {
            toast.success('Banner created successfully!')
            queryClient.invalidateQueries(['admin-banners'])
            setIsModalOpen(false)
            reset()
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to create banner')
        },
    })

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => bannerAPI.updateBanner(id, data),
        onSuccess: () => {
            toast.success('Banner updated successfully!')
            queryClient.invalidateQueries(['admin-banners'])
            setIsModalOpen(false)
            setEditingBanner(null)
            reset()
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to update banner')
        },
    })

    const deleteMutation = useMutation({
        mutationFn: bannerAPI.deleteBanner,
        onSuccess: () => {
            toast.success('Banner deleted successfully!')
            queryClient.invalidateQueries(['admin-banners'])
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to delete banner')
        },
    })

    const handleEdit = (banner) => {
        setEditingBanner(banner)
        const formData = {
            title: banner.title,
            subtitle: banner.subtitle,
            description: banner.description,
            imageUrl: banner.image_url,
            mobileImageUrl: banner.mobile_image_url,
            ctaText: banner.cta_text,
            ctaLink: banner.cta_link,
            ctaType: banner.cta_type,
            order: banner.order,
            active: banner.active,
            position: banner.position
        }
        reset(formData)
        setIsModalOpen(true)
    }

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this banner?')) {
            deleteMutation.mutate(id)
        }
    }

    const onSubmit = (data) => {
        const bannerData = {
            title: data.title,
            subtitle: data.subtitle,
            description: data.description,
            image_url: data.imageUrl,
            mobile_image_url: data.mobileImageUrl,
            cta_text: data.ctaText,
            cta_link: data.ctaLink,
            cta_type: data.ctaType || 'link',
            order: data.order || 0,
            active: data.active || true,
            position: data.position || 'hero'
        }

        if (editingBanner) {
            updateMutation.mutate({ id: editingBanner._id, data: bannerData })
        } else {
            createMutation.mutate(bannerData)
        }
    }

    const handleCloseModal = () => {
        setIsModalOpen(false)
        setEditingBanner(null)
        reset()
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <ErrorMessage message="Failed to load banners" onRetry={refetch} />
            </div>
        )
    }

    const banners = data?.data?.banners || []

    return (
        <>
            <Helmet>
                <title>Manage Banners | Margdarshak Admin</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <div className="min-h-screen bg-gray-50">
                {/* Quick Actions */}
                <div className="bg-white shadow-sm border-b border-gray-200">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="flex justify-end">
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="btn-primary"
                            >
                                <PlusIcon className="w-5 h-5 mr-2" />
                                Add Banner
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {isLoading ? (
                        <div className="flex justify-center py-12">
                            <LoadingSpinner size="lg" />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {banners.map((banner) => (
                                <motion.div
                                    key={banner._id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                                >
                                    <div className="aspect-video bg-gradient-to-br from-primary-600 to-secondary-600 relative">
                                        {banner.image_url ? (
                                            <img
                                                src={banner.image_url}
                                                alt={banner.title}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <PhotoIcon className="w-12 h-12 text-white/50" />
                                            </div>
                                        )}
                                        <div className="absolute inset-0 bg-black/20"></div>
                                        <div className="absolute bottom-4 left-4 right-4 text-white">
                                            <h3 className="text-lg font-bold mb-1 text-white">{banner.title}</h3>
                                            <p className="text-sm opacity-90 text-white">{banner.subtitle}</p>
                                        </div>
                                        <div className="absolute top-4 right-4">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${banner.active
                                                ? 'bg-accent-100 text-accent-800'
                                                : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                {banner.active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div className="text-sm text-gray-500">
                                                Order: {banner.order}
                                            </div>
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => handleEdit(banner)}
                                                    className="text-accent-600 hover:text-accent-900 p-1"
                                                >
                                                    <PencilIcon className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(banner._id)}
                                                    className="text-red-600 hover:text-red-900 p-1"
                                                >
                                                    <TrashIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Add/Edit Modal */}
                <AnimatePresence>
                    {isModalOpen && (
                        <div className="fixed inset-0 z-[9999] overflow-y-auto">
                            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                                <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleCloseModal}></div>

                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl relative z-[10000]"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="flex items-center justify-between mb-6">
                                        <h3 className="text-2xl font-bold text-gray-900">
                                            {editingBanner ? 'Edit Banner' : 'Add New Banner'}
                                        </h3>
                                        <button
                                            onClick={handleCloseModal}
                                            className="p-2 text-gray-400 hover:text-gray-600 relative z-[10001]"
                                        >
                                            <XMarkIcon className="w-6 h-6" />
                                        </button>
                                    </div>

                                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Title *
                                                </label>
                                                <input
                                                    type="text"
                                                    {...register('title', { required: 'Title is required' })}
                                                    className="input"
                                                    placeholder="Enter banner title"
                                                />
                                                {errors.title && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Subtitle
                                                </label>
                                                <input
                                                    type="text"
                                                    {...register('subtitle')}
                                                    className="input"
                                                    placeholder="Enter banner subtitle"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Banner Image
                                                </label>
                                                <ImageUpload
                                                    value={watch('imageUrl') || ''}
                                                    onChange={(value) => setValue('imageUrl', value)}
                                                    placeholder="Upload banner image"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Mobile Banner Image (Optional)
                                                </label>
                                                <ImageUpload
                                                    value={watch('mobileImageUrl') || ''}
                                                    onChange={(value) => setValue('mobileImageUrl', value)}
                                                    placeholder="Upload mobile banner image"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Order
                                                </label>
                                                <input
                                                    type="number"
                                                    {...register('order', { min: 1 })}
                                                    className="input"
                                                    placeholder="Display order"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    CTA Text
                                                </label>
                                                <input
                                                    type="text"
                                                    {...register('ctaText')}
                                                    className="input"
                                                    placeholder="Call to action text"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    CTA Link
                                                </label>
                                                <input
                                                    type="text"
                                                    {...register('ctaLink')}
                                                    className="input"
                                                    placeholder="https://example.com"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    CTA Type
                                                </label>
                                                <select
                                                    {...register('ctaType')}
                                                    className="input"
                                                >
                                                    <option value="link">Link</option>
                                                    <option value="modal">Modal</option>
                                                    <option value="download">Download</option>
                                                    <option value="enquiry">Enquiry</option>
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Position
                                                </label>
                                                <select
                                                    {...register('position')}
                                                    className="input"
                                                >
                                                    <option value="hero">Hero</option>
                                                    <option value="middle">Middle</option>
                                                    <option value="bottom">Bottom</option>
                                                    <option value="sidebar">Sidebar</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Description
                                            </label>
                                            <textarea
                                                {...register('description')}
                                                rows={3}
                                                className="input"
                                                placeholder="Enter banner description"
                                            />
                                        </div>

                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id="active"
                                                {...register('active')}
                                                className="mr-2 h-4 w-4 text-accent-600 focus:ring-accent-500 border-gray-300 rounded"
                                            />
                                            <label htmlFor="active" className="text-sm text-gray-700">
                                                Active Banner
                                            </label>
                                        </div>

                                        <div className="flex justify-end space-x-4">
                                            <button
                                                type="button"
                                                onClick={handleCloseModal}
                                                className="btn-secondary"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={createMutation.isPending || updateMutation.isPending}
                                                className="btn-primary"
                                            >
                                                {createMutation.isPending || updateMutation.isPending ? (
                                                    <div className="flex items-center space-x-2">
                                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                        <span>Saving...</span>
                                                    </div>
                                                ) : (
                                                    editingBanner ? 'Update Banner' : 'Create Banner'
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </motion.div>
                            </div>
                        </div>
                    )}
                </AnimatePresence>
            </div>
        </>
    )
}

export default AdminBanners
